import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type FormEvent,
  type KeyboardEvent
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownIcon,
  BookOpenIcon,
  ExternalLinkIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  XIcon
} from 'lucide-react';
import { ComposerSendIcon } from '@/components/assistant-ui/composer-send-icon';
import { Button } from '@/components/ui/button';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/utils/toast';
import {
  toMcpApiErrorMessage,
  useAskMcpHelpMutation,
  useUpsertMcpMessageFeedbackMutation
} from '../api/mcpApi';
import type {
  McpHelpMessageParts,
  McpHelpSource,
  McpThreadMessage
} from '../types/mcpApiTypes';
import {
  MCP_HELP_SUGGESTIONS,
  type McpHelpSuggestion
} from '../config/mcpHelpSuggestions';
import { useStartNewMcpChat } from '../hooks/useStartNewMcpChat';
import { invalidateMcpThreadHistory } from '../utils/threadHistoryEvents';
import { useMcpHelpMode } from './McpHelpModeContext';

type HelpFeedbackRating = 'positive' | 'negative';

type HelpMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'assistant';
      text: string;
      sources: McpHelpSource[];
      feedback: HelpFeedbackRating | null;
    };

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseHelpParts(
  parts: Record<string, unknown> | null | undefined
): McpHelpMessageParts | null {
  if (!parts || parts.kind !== 'help') return null;
  return parts as unknown as McpHelpMessageParts;
}

export function isHelpThreadMessages(messages: McpThreadMessage[]): boolean {
  return messages.some(m => parseHelpParts(m.parts) != null);
}

export function toHelpMessages(messages: McpThreadMessage[]): HelpMessage[] {
  return messages
    .filter(
      m =>
        (m.role === 'user' || m.role === 'assistant') &&
        (m.content ?? '').trim()
    )
    .map(m => {
      if (m.role === 'user') {
        return { id: m.id, role: 'user' as const, text: m.content!.trim() };
      }
      const parts = parseHelpParts(m.parts);
      const feedback =
        m.myFeedback?.rating === 'positive' ||
        m.myFeedback?.rating === 'negative'
          ? m.myFeedback.rating
          : null;
      return {
        id: m.id,
        role: 'assistant' as const,
        text: m.content!.trim(),
        sources: parts?.sources ?? [],
        feedback
      };
    });
}

type McpHelpChatProps = {
  threadId?: string;
  initialMessages?: HelpMessage[];
  onThreadCreated?: (id: string) => void;
};

/** Compact mode chip beside send — dismiss with × to leave Help. */
const HelpModeBadge: FC = () => {
  const startNewChat = useStartNewMcpChat();
  const { exitHelpMode } = useMcpHelpMode();

  return (
    <div
      dir="ltr"
      className="border-blue-500/40 bg-blue-500/15 text-blue-700 dark:border-blue-400/45 dark:bg-blue-500/25 dark:text-blue-200 inline-flex shrink-0 items-center gap-1.5 rounded-full border py-1.5 ps-3 pe-1.5 text-xs font-semibold leading-none select-none"
    >
      <span>راهنما</span>
      <button
        type="button"
        onClick={() => {
          exitHelpMode();
          startNewChat();
        }}
        aria-label="خروج از راهنما"
        title="خروج از راهنما"
        className="text-inherit !m-0 !inline-flex !size-6 !shrink-0 !items-center !justify-center !rounded-full !border-0 !bg-transparent !p-0 !shadow-none !outline-none !ring-0 hover:!bg-blue-500/20 focus-visible:!outline-none focus-visible:!ring-0"
      >
        <XIcon className="size-3.5 opacity-80" strokeWidth={2.25} aria-hidden />
      </button>
    </div>
  );
};

const HelpWelcome: FC = () => (
  <div className="aui-thread-welcome-root mb-6 flex flex-col items-center px-4 text-center">
    <div className="border-border bg-primary/10 text-foreground mb-3 inline-flex size-11 items-center justify-center rounded-xl border">
      <BookOpenIcon size={22} strokeWidth={1.75} aria-hidden />
    </div>
    <h2 className="aui-thread-welcome-message-inner text-2xl font-medium tracking-tight">
      چطور کمکتون کنم؟
    </h2>
    <p className="text-muted-foreground mt-2 max-w-sm text-sm leading-relaxed">
      درباره محدودیت‌ها، نحوه استفاده از ابزارها و تنظیمات بپرسید.
    </p>
  </div>
);

type HelpSuggestionsProps = {
  onPick: (item: McpHelpSuggestion) => void;
  disabled?: boolean;
};

const HelpSuggestions: FC<HelpSuggestionsProps> = ({ onPick, disabled }) => (
  <div
    className="flex w-full flex-col gap-2 px-1"
    dir="rtl"
    aria-label="سوالات پیشنهادی راهنما"
  >
    <div className="flex items-baseline justify-between gap-2 px-0.5">
      <span className="text-foreground text-xs font-semibold">
        سؤالات پرتکرار
      </span>
      <span className="text-muted-foreground text-[0.7rem]">
        یک مورد را انتخاب کنید
      </span>
    </div>
    <div className="grid w-full grid-cols-2 gap-2">
      {MCP_HELP_SUGGESTIONS.map(item => (
        <Button
          key={item.id}
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => onPick(item)}
          className={cn(
            'text-foreground hover:bg-muted border-border/60 h-full min-h-0 w-full flex-col items-start justify-start gap-1 rounded-xl px-3 py-2.5 text-start font-normal whitespace-normal shadow-xs'
          )}
        >
          <span className="text-sm leading-snug">{item.label}</span>
        </Button>
      ))}
    </div>
  </div>
);

type HelpAssistantMessageProps = {
  message: Extract<HelpMessage, { role: 'assistant' }>;
  onFeedback: (id: string, rating: HelpFeedbackRating) => void;
};

const HelpAssistantMessage: FC<HelpAssistantMessageProps> = ({
  message,
  onFeedback
}) => {
  const rating = message.feedback ?? null;

  return (
    <div
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 animate-in w-full px-2 duration-150"
      data-role="assistant"
      dir="rtl"
    >
      <div className="aui-assistant-message-content text-foreground wrap-break-word text-[0.85rem] leading-[1.95] whitespace-pre-wrap">
        {message.text}
      </div>

      {message.sources.length > 0 ? (
        <div className="mt-5 flex flex-col gap-2" aria-label="منابع" dir="rtl">
          <span className="text-muted-foreground text-[0.7rem] font-semibold">
            منابع
          </span>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
            {message.sources.map(source => (
              <li key={source.url}>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  dir="ltr"
                  className="border-border bg-card text-foreground hover:bg-accent max-w-64 items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[0.72rem] no-underline"
                >
                  <ExternalLinkIcon
                    className="me-1.5 size-3 shrink-0 opacity-70"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <span dir="rtl" className="min-w-0 truncate [unicode-bidi:plaintext]">
                    {source.title?.trim() || source.url}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="aui-assistant-message-footer mt-3 flex min-h-6 items-center gap-0.5 overflow-visible px-1">
        <TooltipIconButton
          tooltip="پاسخ خوب"
          side="top"
          variant="ghost"
          className={cn(
            'text-muted-foreground size-6 hover:bg-transparent',
            rating === 'positive' &&
              'text-[var(--falcon-primary)] hover:text-[var(--falcon-primary)]'
          )}
          onClick={() => onFeedback(message.id, 'positive')}
        >
          <ThumbsUpIcon
            className={cn('size-3.5', rating === 'positive' && 'fill-current')}
          />
        </TooltipIconButton>
        <TooltipIconButton
          tooltip="پاسخ بد"
          side="top"
          variant="ghost"
          className={cn(
            'text-muted-foreground size-6 hover:bg-transparent',
            rating === 'negative' &&
              'text-[var(--falcon-primary)] hover:text-[var(--falcon-primary)]'
          )}
          onClick={() => onFeedback(message.id, 'negative')}
        >
          <ThumbsDownIcon
            className={cn('size-3.5', rating === 'negative' && 'fill-current')}
          />
        </TooltipIconButton>
      </div>
    </div>
  );
};

const HelpUserMessage: FC<{ text: string }> = ({ text }) => (
  <div
    className="aui-user-message-root fade-in slide-in-from-bottom-1 animate-in w-full px-2 duration-150"
    data-role="user"
  >
    <div className="aui-user-message-content-wrapper">
      <div className="aui-user-message-content peer bg-muted text-foreground rounded-xl px-4 py-3 text-[0.85rem] leading-[1.95] wrap-break-word">
        {text}
      </div>
    </div>
  </div>
);

/**
 * Help chat (FAQ) — persists via MCP `/chat/help/ask` + message-feedback.
 */
export const McpHelpChat: FC<McpHelpChatProps> = ({
  threadId: threadIdProp,
  initialMessages = [],
  onThreadCreated
}) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<HelpMessage[]>(initialMessages);
  const [draft, setDraft] = useState('');
  const [threadId, setThreadId] = useState<string | undefined>(threadIdProp);
  const listEndRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const [askHelp, { isLoading: sending }] = useAskMcpHelpMutation();
  const [upsertFeedback] = useUpsertMcpMessageFeedbackMutation();

  const isEmpty = messages.length === 0;

  const updateScrollButton = useCallback(() => {
    const el = viewportRef.current;
    if (!el) {
      setShowScrollToBottom(false);
      return;
    }
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollToBottom(distance > 96);
  }, []);

  useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  useEffect(() => {
    setThreadId(threadIdProp);
  }, [threadIdProp]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    window.requestAnimationFrame(updateScrollButton);
  }, [messages.length, sending, updateScrollButton]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    updateScrollButton();
    el.addEventListener('scroll', updateScrollButton, { passive: true });
    window.addEventListener('resize', updateScrollButton);
    return () => {
      el.removeEventListener('scroll', updateScrollButton);
      window.removeEventListener('resize', updateScrollButton);
    };
  }, [updateScrollButton]);

  const scrollToBottom = () => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const sendQuestion = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || sending) return;

      const pendingUserId = uid();
      setDraft('');
      setMessages(prev => [
        ...prev,
        { id: pendingUserId, role: 'user', text: trimmed }
      ]);

      try {
        const result = await askHelp({
          question: trimmed,
          threadId
        }).unwrap();

        setMessages(prev => {
          const withoutPending = prev.filter(m => m.id !== pendingUserId);
          return [
            ...withoutPending,
            {
              id: result.userMessage.id,
              role: 'user',
              text: result.userMessage.content
            },
            {
              id: result.assistantMessage.id,
              role: 'assistant',
              text: result.assistantMessage.content,
              sources: result.assistantMessage.parts?.sources ?? [],
              feedback: null
            }
          ];
        });

        if (result.threadId !== threadId) {
          setThreadId(result.threadId);
          onThreadCreated?.(result.threadId);
          invalidateMcpThreadHistory();
          navigate(`/chat/c/${result.threadId}`, { replace: true });
        } else {
          invalidateMcpThreadHistory();
        }
      } catch (err) {
        setMessages(prev => prev.filter(m => m.id !== pendingUserId));
        errorMessage(toMcpApiErrorMessage(err, 'دریافت پاسخ راهنما ناموفق بود'));
      } finally {
        inputRef.current?.focus();
      }
    },
    [askHelp, navigate, onThreadCreated, sending, threadId]
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void sendQuestion(draft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendQuestion(draft);
    }
  };

  const onFeedback = (id: string, rating: HelpFeedbackRating) => {
    const previous = messages.find(
      m => m.role === 'assistant' && m.id === id
    ) as Extract<HelpMessage, { role: 'assistant' }> | undefined;
    setMessages(prev =>
      prev.map(m =>
        m.role === 'assistant' && m.id === id ? { ...m, feedback: rating } : m
      )
    );
    void upsertFeedback({ messageId: id, rating })
      .unwrap()
      .catch(err => {
        setMessages(prev =>
          prev.map(m =>
            m.role === 'assistant' && m.id === id
              ? { ...m, feedback: previous?.feedback ?? null }
              : m
          )
        );
        errorMessage(toMcpApiErrorMessage(err, 'ثبت بازخورد ناموفق بود'));
      });
  };

  return (
    <div
      className="aui-root aui-thread-root bg-background @container flex h-full min-h-0 flex-col"
      style={{
        ['--thread-max-width' as string]: '50rem',
        ['--composer-bg' as string]: 'var(--color-card)',
        ['--composer-radius' as string]: '1.75rem',
        ['--composer-padding' as string]: '4px'
      }}
    >
      <div
        ref={viewportRef}
        data-slot="aui_thread-viewport"
        className="relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth"
      >
        <div
          className={cn(
            'mx-auto flex w-full max-w-(--thread-max-width) min-w-0 flex-1 flex-col px-4 pt-4 sm:px-6',
            isEmpty && 'justify-center'
          )}
        >
          {isEmpty ? <HelpWelcome /> : null}

          <div
            data-slot="aui_message-group"
            className="mb-14 flex flex-col gap-y-10 empty:hidden"
          >
            {messages.map(m =>
              m.role === 'user' ? (
                <HelpUserMessage key={m.id} text={m.text} />
              ) : (
                <HelpAssistantMessage
                  key={m.id}
                  message={m}
                  onFeedback={onFeedback}
                />
              )
            )}
            {sending ? (
              <div
                className="text-muted-foreground px-2 text-sm"
                dir="rtl"
                aria-live="polite"
              >
                در حال دریافت پاسخ…
              </div>
            ) : null}
            <div ref={listEndRef} />
          </div>

          <div
            data-slot="aui_thread-viewport-footer"
            className={cn(
              'aui-thread-viewport-footer bg-background relative flex flex-col gap-4 overflow-visible pb-4 md:pb-6',
              !isEmpty &&
                'sticky bottom-0 mt-auto rounded-t-(--composer-radius)'
            )}
          >
            {showScrollToBottom ? (
              <div className="pointer-events-none absolute inset-x-0 -top-11 z-30 flex justify-center">
                <TooltipIconButton
                  tooltip="رفتن به آخر گفتگو"
                  side="top"
                  variant="outline"
                  className="aui-thread-scroll-to-bottom pointer-events-auto rounded-full"
                  onClick={scrollToBottom}
                >
                  <ArrowDownIcon />
                </TooltipIconButton>
              </div>
            ) : null}
            <form
              onSubmit={onSubmit}
              className="relative flex w-full min-w-0 flex-col"
              dir="rtl"
            >
              <div
                data-slot="aui_composer-shell"
                className="mcp-help-composer-shell flex w-full min-w-0 flex-col"
              >
                <div className="flex w-full min-w-0 items-end gap-2 px-2.5 py-2">
                  <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    disabled={sending}
                    placeholder="سؤال خود را درباره محصول بپرسید…"
                    aria-label="سؤال راهنما"
                    className="aui-composer-input max-h-40 min-h-10 flex-1 resize-none bg-transparent px-1.5 py-2 text-sm outline-none"
                  />
                  <div
                    className="aui-composer-action-wrapper relative flex shrink-0 items-center gap-2 self-center"
                    dir="ltr"
                  >
                    <Button
                      type="submit"
                      size="icon"
                      disabled={sending || !draft.trim()}
                      className="aui-composer-send size-8 shrink-0 rounded-full"
                      aria-label="ارسال"
                    >
                      <ComposerSendIcon className="size-4" />
                    </Button>
                    <HelpModeBadge />
                  </div>
                </div>
              </div>
            </form>

            {isEmpty ? (
              <HelpSuggestions
                disabled={sending}
                onPick={item => void sendQuestion(item.question)}
              />
            ) : null}

            <p className="text-center text-600 fs--2 mb-0">
              راهنما بر اساس دانش‌نامه FAQ پاسخ می‌دهد و ممکن است کامل نباشد.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
